import { appConfig } from "app-config";
import { flagConstants, localStorageConstants } from "constants/index";
import { StoreContext } from "contexts";
import { EvaluatedFlags, FlagsupClientBuilder } from "flagsup-ts";
import { IRoute } from "models";
import { useContext, useEffect, useState, useCallback } from "react";
import { IUserDetailsInfo } from "teko-oauth2";
import { flagUtils, trackingUtils } from "utils";

const { serviceHost, env } = appConfig;
const { FLAG_KEYS, EXTENDED_FLAG_KEYS } = flagConstants;
const { FLAG_ECOM_489 } = localStorageConstants;

const FLAGSUP_CLIENT = new FlagsupClientBuilder()
  .setTargetHostname(serviceHost.flagsup)
  .setMaxRetries(3)
  .setAllowUseStaleTime(30 * 60) // 30 min in seconds
  .setEnableLogging(env === "DEV");

const useFilterNavigationByFeatureFlags = (items: IRoute[]) => {
  const { featureFlagsData } = useContext(StoreContext);

  // Get navigation which their feature flags was enable to build menu
  const filteredNavigation = flagUtils.filterFlagsEnabled(items, featureFlagsData);

  // Only get routes which is link to a component
  const filteredRoutes = filteredNavigation.filter((item) => !item.children && item.component);

  return { filteredNavigation, filteredRoutes };
};

const useFeatureFlags = (currentUser?: IUserDetailsInfo) => {
  const [featureFlagsData, setFeatureFlagsData] = useState<EvaluatedFlags>({});
  const [isInitiating, setIsInitiating] = useState(true);

  const getFlags = useCallback(async (flagClient: FlagsupClientBuilder, userDetail: IUserDetailsInfo) => {
    setIsInitiating(true);
    const startAt = performance.now();
    const { error, flags } = await flagClient.getFlags(
      {
        ...FLAG_KEYS,
        ...EXTENDED_FLAG_KEYS,
      },
      userDetail
    );

    if (error) {
      trackingUtils.nonBlockTrackRespTimeWithStartAt("batch-eval-flags-error", startAt, {
        error,
      });
    }

    if (flags) {
      trackingUtils.nonBlockTrackRespTimeWithStartAt("batch-eval-flags", startAt, {
        flagData: flags,
      });

      localStorage.setItem(FLAG_ECOM_489, flags[EXTENDED_FLAG_KEYS.EPIC_ECOM_489.key]?.enabled ? "true" : "false");
      setFeatureFlagsData(flags);
    }
    setIsInitiating(false);
  }, []);

  // Setup extra config and subscribe to flag changes
  useEffect(() => {
    if (!currentUser?.sub) {
      return;
    }

    const unsubscribe = FLAGSUP_CLIENT.setDefaultEntityId(currentUser.sub).subscribe((updatedFlags) => {
      localStorage.setItem(
        FLAG_ECOM_489,
        updatedFlags[EXTENDED_FLAG_KEYS.EPIC_ECOM_489.key]?.enabled ? "true" : "false"
      );
      setFeatureFlagsData(updatedFlags);
    });

    // Start to fetch flags
    getFlags(FLAGSUP_CLIENT, currentUser);

    return () => {
      unsubscribe();
    };
  }, [currentUser?.sub]);

  return { featureFlagsData, isInitiating };
};

export default {
  useFeatureFlags,
  useFilterNavigationByFeatureFlags,
};
